import clsx from 'clsx';
import { Navigate } from 'react-router';
import { useZonesMapView } from '../hooks/use-zones-map-view';
import { ZoneKindLegend } from './ZoneKindLegend';
import { ZonePanel } from '../../editor/components/ZonePanel';
import { ZonesMapArea } from './ZonesMapArea';
import { ZonesMapToolbar } from './ZonesMapToolbar';

/** Most of the viewport on a wide screen, beside the panel; a fixed share of it on a phone, with the panel below. */
const MAP_AREA = 'h-[60dvh] min-h-[22rem] lg:h-[calc(100dvh-17rem)] lg:min-h-[30rem]';

/**
 * Every zone on one map, coloured by kind, with search and filters above it. A zone opens in a panel
 * beside the map (`openZoneId`), where its shape is edited on this same map with the others still around it.
 */
export function ZonesMapView({ openZoneId }: { openZoneId?: string }) {
  const view = useZonesMapView(openZoneId);

  if (view.zoneKindsState.status === 'forbidden') return <Navigate to="/" replace />;

  return (
    <div className="flex flex-col gap-4">
      <ZonesMapToolbar list={view.list} zoneKinds={view.kinds} onPickZone={view.navigation.openZone} />
      <div className={clsx('grid gap-4', openZoneId && 'lg:grid-cols-[minmax(0,1fr)_22rem]')}>
        <section aria-label="Zone map" className="flex min-w-0 flex-col gap-2">
          <div className={clsx('relative', MAP_AREA)}>
            <ZonesMapArea tileKey={view.tileKey} containerRef={view.containerRef} state={view.mapZones.state} />
          </div>
          <ZoneKindLegend counts={view.legend} />
        </section>
        {openZoneId && (
          <ZonePanel
            key={openZoneId}
            zoneId={openZoneId}
            map={view.map}
            zoneKinds={view.zoneKindsState.status === 'loaded' ? view.kinds : null}
            onChanged={view.mapZones.reload}
            onClose={view.navigation.closeZone}
            onOpenZone={view.navigation.openZone}
          />
        )}
      </div>
    </div>
  );
}
